import React, { useState, useEffect } from "react";
import createAxiosInstance from "../../../utils/axiosConfig";
import { create_framework, edit_framework } from "../../../utils/endpoints";
import { FrameworkCategory } from "../../../utils/interfaces";

interface AddFrameworkModalProps {
    show: boolean;
    onClose: () => void;
    onSuccess: (frameworkData?: FrameworkCategory) => void;
    isEditMode?: boolean;
    editData?: FrameworkCategory | null;
    onEditScoringOptions?: (frameworkId: number) => void;
}

interface FormData {
    title: string;
    description: string;
    is_enabled: boolean;
    is_deleted: boolean;
    custom_prompts_placeholder?: string;
    id?: number;
}

const AddFrameworkModal: React.FC<AddFrameworkModalProps> = ({
    show,
    onClose,
    onSuccess,
    isEditMode = false,
    editData = null,
    onEditScoringOptions
}) => {
    const [userData, setUserData] = useState<any>(null);

    useEffect(() => {
        const storedUserData = localStorage.getItem('userData');
        if (storedUserData) {
            setUserData(JSON.parse(storedUserData));
        }
    }, []);
    const [formData, setFormData] = useState<FormData>({
        title: "",
        description: "",
        is_enabled: true,
        is_deleted: false,
        custom_prompts_placeholder: "",
        id: undefined
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Pre-populate form data when in edit mode
    useEffect(() => {
        if (isEditMode && editData) {
            setFormData({
                title: editData.title || "",
                description: editData.description || "",
                is_enabled: editData.is_enabled || false,
                is_deleted: false, // Always default to false for safety
                custom_prompts_placeholder: editData.custom_prompts_placeholder || "",
                id: editData.id
            });
        } else {
            // Reset form for create mode
            setFormData({
                title: "",
                description: "",
                is_enabled: true,
                is_deleted: false,
                custom_prompts_placeholder: "",
                id: undefined
            });
        }
    }, [isEditMode, editData, show]);

    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        
        if (!formData.title.trim()) {
            newErrors.title = "Title is required";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validate()) return;
        
        setLoading(true);
        
        try {
            const axiosInstance = createAxiosInstance();
            let response;
            
            if (isEditMode && editData) {
                // Edit mode - use PUT request
                response = await axiosInstance.put(edit_framework(editData.id), formData);
            } else {
                // Create mode - use POST request
                response = await axiosInstance.post(create_framework, formData);
            }
            
            if (response.status === 200 || response.status === 201) {
                // Pass the actual framework data from the nested response structure
                onSuccess(response.data.data || response.data);
                handleClose();
            }
        } catch (error: any) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} framework:`, error);
            // You can add error handling here if needed
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            title: "",
            description: "",
            is_enabled: true,
            is_deleted: false,
            custom_prompts_placeholder: "",
            id: undefined
        });
        setErrors({});
        setLoading(false);
        onClose();
    };

    if (!show) return null;

    return (
        <>
            <div
                className="modal-overlay"
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 999,
                }}
                onClick={handleClose}
            ></div>

            <div
                className="modal card fadeIn"
                style={{
                    display: "block",
                    position: "fixed",
                    maxWidth: "500px",
                    maxHeight: "max-content",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    border: "1px solid #dee2e6",
                    borderRadius: "1.875rem",
                    overflow: "hidden"
                }}
            >
                <div className="modal-header mx-4" style={{
                    borderBottomColor: '#5376F0'
                }}>
                    <h5 className="modal-title" style={{
                        color: '#26397E',
                        fontSize: '1.625rem',
                        fontWeight: 'bold'
                    }}>
                        {isEditMode ? 'Edit Framework' : 'Add New Framework'}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        disabled={loading}
                    ></button>
                </div>
                
                <div className="modal-body mx-4">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label">
                                Title:
                            </label>
                            <input
                                type="text"
                                className={`form-control bg-white ${errors.title ? 'is-invalid' : ''}`}
                                id="title"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                disabled={loading}
                                // placeholder="Enter framework title"
                            />
                            {errors.title && (
                                <div className="invalid-feedback">{errors.title}</div>
                            )}
                        </div>
                        
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label">
                                Description:
                            </label>
                            <textarea
                                className="form-control bg-white"
                                id="description"
                                name="description"
                                rows={3}
                                value={formData.description}
                                onChange={handleChange}
                                disabled={loading}
                                // placeholder="Enter framework description (optional)"
                            />
                        </div>
                        
                        {(userData?.is_admin === 1 || userData?.account_type === 5) && (
                            <div className="mb-3">
                                <label htmlFor="enhance_ai_prompts" className="form-label">
                                    Enhance AI Prompts
                                </label>
                                <textarea
                                    className="form-control bg-white"
                                    id="custom_prompts_placeholder"
                                    name="custom_prompts_placeholder"
                                    rows={4}
                                    value={formData.custom_prompts_placeholder}
                                    onChange={handleChange}
                                    placeholder="Enter prompts to enhance AI responses for this framework..."
                                    disabled={loading}
                                />
                            </div>
                        )}

                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="is_enabled"
                                    name="is_enabled"
                                    checked={formData.is_enabled}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="is_enabled">
                                    Enable Framework
                                </label>
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="is_deleted"
                                    name="is_deleted"
                                    checked={formData.is_deleted}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="is_deleted">
                                    Mark as Deleted
                                </label>
                            </div>
                        </div>

                        {(userData?.is_admin === 1 || userData?.account_type === 5) && isEditMode && (
                            <div className="mb-3">
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-none"
                                    onClick={() => {
                                        if (onEditScoringOptions && formData.id) {
                                            onClose();
                                            onEditScoringOptions(formData.id);
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    Edit Scoring Options
                                </button>
                            </div>
                        )}
                        
                        <div className="d-flex justify-content-end gap-2 pb-4">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn"
                                disabled={loading || Object.keys(errors).length > 0}
                                style={{
                                    backgroundColor: loading ? '#6c757d' : '#183FCE',
                                    color: 'white',
                                    border: 'none',
                                    minWidth: '120px'
                                }}
                            >
                                {loading ? (
                                    <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{ height: "20px" }}
                                    >
                                        <div className="spinner-border text-white" role="status" style={{ width: '16px', height: '16px' }}>
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    isEditMode ? "Update Framework" : "Save Framework"
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default AddFrameworkModal; 